import type {
    DesignProjectCreateBody,
    DesignProjectCreateResponse,
    DesignProjectSelect,
    DesignerStartType,
} from '~~/layers/base/types/designProject'
import { useApiFetch } from '@/composables/useApiFetch'
/**
 * Design Project Service
 *
 * Handles all design project operations for the Crane Designer system.
 * This service provides methods to create, retrieve, and update design projects
 * that store user configurations and template snapshots.
 *
 * Key Features:
 * - Create new design projects with template snapshots
 * - Retrieve existing design projects by ID
 * - Update design project activity timestamps
 * - Integrates with server API endpoints for database operations
 *
 * Usage:
 * ```typescript
 * const { doCreateNewDesignProject, doGetDesignProjectById } = useDesignProjectService()
 *
 * // Create a new design project
 * const project = await doCreateNewDesignProject({
 *   sku: 'ABC123',
 *   configTemplateId: 'uuid',
 *   fingerprintId: 'user-fingerprint'
 * })
 *
 * // Get existing design project
 * const existingProject = await doGetDesignProjectById('project-uuid')
 * ```
 */

export const useDesignProjectService = () => {
    // // Get the runtime configuration (from nuxt.config or environment variables)
    // const runtimeConfig = useRuntimeConfig() as {
    //     public?: {
    //         // Optional public config key for the design token cookie name
    //         NUXT_MY_APP_DESIGN_TOKEN_COOKIE?: string
    //     }
    // }

    // // Extract the design token cookie name from the public runtime config
    // const designTokenCookieName = runtimeConfig.public?.NUXT_MY_APP_DESIGN_TOKEN_COOKIE as string

    // // Read the actual design token cookie value using the extracted cookie name
    // const designTokenCookie = useAppCookie<string>(designTokenCookieName)

    /**
     * Create a new design project
     *
     * Creates a new design project with template snapshot and user configuration.
     * The project will be stored in the database with all required fields
     * including user identification, product context, and project metadata.
     *
     * @param params - Design project creation parameters including SKU, template ID, and user context
     * @returns Promise with design project data or error information
     */
    const doCreateNewDesignProject = async (
        params: DesignProjectCreateBody,
        options?: { signal?: AbortSignal }
    ) => {
        const { data, error, pending, refresh } = await useApiFetch<DesignProjectCreateResponse>(
            '/api/design_project',
            {
                method: 'POST',
                body: params,
                signal: options?.signal,
            }
        )
        return { data, error, pending, refresh }
    }

    const doGetDesignProjectForPersonalize = async (type: DesignerStartType, id: string) => {
        const { data, error, pending, refresh } = await useApiFetch<DesignProjectSelect>(
            `/api/design_project/personalize/${type}/${id}`,
            { method: 'GET' }
        )
        return { data, error, pending, refresh }
    }

    /**
     * Retrieve a design project by its unique identifier
     *
     * Fetches an existing design project from the database using the project ID.
     * Returns the complete project data including configuration and metadata.
     *
     * @param projectId - Unique design project identifier (UUID)
     * @returns Promise with design project data or error information
     */
    const doGetDesignProjectById = async (projectId: string) => {
        //      // Get the value of the design token cookie, or undefined if it doesn't exist
        // const token = designTokenCookie.get() || undefined

        // // Conditionally add the Authorization header if the token exists
        // headers: {
        //     ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // },
        const { data, error, pending, refresh } = await useApiFetch<DesignProjectSelect>(
            `/api/design_project/${projectId}`,
            {
                method: 'GET',
            }
        )
        return { data, error, pending, refresh }
    }

    /**
     * Update design project activity timestamp
     *
     * Updates the last_activity_at field to track user engagement.
     * This is used for project management and cleanup of inactive projects.
     *
     * @param projectId - Unique design project identifier (UUID)
     * @returns Promise with update confirmation or error information
     */
    const doUpdateDesignProject = async (projectId: string, body: Partial<DesignProjectSelect>) => {
        //      // Get the value of the design token cookie, or undefined if it doesn't exist
        // const token = designTokenCookie.get() || undefined

        // // Conditionally add the Authorization header if the token exists
        // headers: {
        //     ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // },
        const { data, error, pending, refresh } = await useApiFetch<DesignProjectSelect>(
            `/api/design_project/${projectId}`,
            {
                method: 'PATCH',
                body,
            }
        )
        return { data, error, pending, refresh }
    }

    return {
        doCreateNewDesignProject,
        doGetDesignProjectById,
        doGetDesignProjectForPersonalize,
        doUpdateDesignProject,
    }
}
